import * as React from 'react'
import ReactTable, { TableProps, Column, CellInfo, Filter, RowInfo } from 'react-table'
import ReactDOM from 'react-dom'
import { DownTime } from '../../model/DownTime'
import { LockStatus } from '../../model/DownTimeEnums'
import { filterScadaObject } from './Downtimes'

interface DispatchProps {
  dateFormatter: (date: Date) => JSX.Element
}

interface StateProps<D extends DownTime> extends Partial<TableProps<D>> {
  showScadaObjectsColumn: boolean
  getRowClasses: (rowInfo?: RowInfo) => string
  overrideFilter: (visible: boolean, filter: Filter, row: any) => boolean
  extraColumns: Array<Column<D>>
}

type State = {}

type Props<D extends DownTime> = DispatchProps & StateProps<D>

export const timeProps = () => ({ title: 'All times are in the site timezone' })

var totalMinutes = (hours: number, minutes: number) => hours * 60 + minutes
var dateTimeOfDay = (date: Date) => totalMinutes(date.getHours(), date.getMinutes())
var stringsTimeOfDay = (parts: string[]) => totalMinutes(parseInt(parts[0]), parseInt(parts[1]))
var stringTimeOfDay = (date: string) => stringsTimeOfDay(date.split(':'))

export class ReadOnlyEventList<D extends DownTime> extends React.Component<Props<D>, State> {
  static defaultProps = {
    showScadaObjectsColumn: false,
    getRowClasses: (rowInfo?: RowInfo) => '',
    overrideFilter: (visible: boolean, filter: Filter, row: any) => visible,
    extraColumns: []
  }
  dataTableElement?: ReactTable<D>

  // see https://github.com/tannerlinsley/react-table/issues/192#issuecomment-442270734
  setupFilters = () => {
    document.querySelectorAll<HTMLInputElement>('div.rt-th > input').forEach(filter => {
      filter.placeholder = 'Filter..'
      filter.title = 'Only show events that contain the specified text in this column'
    })
    document.querySelectorAll<HTMLInputElement>('div.rt-th.time-on > input').forEach(filter => {
      filter.type = 'time'
      filter.title = 'Hide events that start before this time of day or cover multiple days'
    })
    document.querySelectorAll<HTMLInputElement>('div.rt-th.time-off > input').forEach(filter => {
      filter.type = 'time'
      filter.title = 'Hide events that start after this time of day or cover multiple days'
    })
  }

  componentDidMount(): void {
    this.setupFilters()
  }

  componentDidUpdate(previousProps: Readonly<Props<D>>, previousState: Readonly<State>): void {
    if (this.dataTableElement) {
      let theads = (ReactDOM.findDOMNode(this.dataTableElement) as Element).getElementsByClassName(
        'rt-thead'
      )
      let tbody = (ReactDOM.findDOMNode(this.dataTableElement) as Element).getElementsByClassName(
        'rt-tbody'
      )[0]

      if (tbody.scrollHeight > tbody.clientHeight) {
        Array.from(theads).map(t => t.classList.add('vertical-scrollbar-present'))
      } else {
        Array.from(theads).map(t => t.classList.remove('vertical-scrollbar-present'))
      }
    }
  }

  public render() {
    const {
      dateFormatter,
      showScadaObjectsColumn,
      getRowClasses,
      overrideFilter,
      extraColumns,
      ...props
    } = this.props
    return (
      <ReactTable<D>
        showPagination={false}
        showPaginationBottom={false}
        pageSize={props.data ? props.data.length : 0}
        {...props}
        ref={element => {
          if (element) {
            // from https://github.com/tannerlinsley/react-table/issues/495#issuecomment-331946312
            this.dataTableElement = element
            let theads = (ReactDOM.findDOMNode(element) as Element).getElementsByClassName(
              'rt-thead'
            )
            let tbody = (ReactDOM.findDOMNode(element) as Element).getElementsByClassName(
              'rt-tbody'
            )[0]

            tbody.addEventListener('scroll', () => {
              for (let i = 0; i < theads.length; i++) {
                theads[i].scrollLeft = tbody.scrollLeft
              }
            })
          }
        }}
        className={'fixed-height-data-table grow parent'}
        getTheadTrProps={state => ({ style: { minWidth: state.rowMinWidth } })}
        getTrProps={(state: any, rowInfo: any) => ({
          style: { minWidth: state.rowMinWidth },
          className:
            'wordwrap ' +
            getRowClasses(rowInfo) +
            (rowInfo &&
            (rowInfo.original.Edited() ||
              rowInfo.original.lockStatus !== LockStatus.notLocked ||
              rowInfo.original.outstandingEdits)
              ? ' modified'
              : '')
        })}
        columns={[
          ...extraColumns,
          ...(showScadaObjectsColumn
            ? [
                {
                  accessor: 'scadaObjectKey',
                  Header: 'Object Name',
                  Cell: (row: CellInfo) => (
                    <span className="wordwrap">{(row.original as DownTime).scadaObjectName}</span>
                  ),
                  filterMethod: (filter: Filter, row: any) =>
                    overrideFilter(
                      filterScadaObject(filter, row),
                      filter,
                      row
                    )
                }
              ]
            : []),
          {
            accessor: 'comment',
            Header: 'Comment'
          },
          {
            accessor: 'description',
            Header: 'Description',
            Cell: row => <span className="wordwrap">{row.value}</span>
          },
          {
            accessor: 'alarmCode',
            Header: 'Alarm Code',
            Cell: row => <span className="wordwrap">{row.value}</span>
          },
          {
            accessor: 'timeOn',
            Header: 'Time On',
            getProps: timeProps,
            getHeaderProps: timeProps,
            Cell: row => dateFormatter(row.value),
            minWidth: 120,
            maxWidth: 120,
            filterMethod: (filter: Filter, row: any) =>
            overrideFilter(
              row._original.timeOff.toDateString() === row._original.timeOn.toDateString() &&
              dateTimeOfDay(row._original.timeOn) >= stringTimeOfDay(filter.value),
              filter,
              row
            ),
            headerClassName: 'time-on'
          },
          {
            accessor: 'timeOff',
            Header: 'Time Off',
            getProps: timeProps,
            getHeaderProps: timeProps,
            Cell: row => dateFormatter(row.value),
            minWidth: 120,
            maxWidth: 120,
            filterMethod: (filter: Filter, row: any) =>
            overrideFilter(
              row._original.timeOff &&
              row._original.timeOff.toDateString() === row._original.timeOn.toDateString() &&
              dateTimeOfDay(row._original.timeOff) <= stringTimeOfDay(filter.value),
              filter,
              row),
            headerClassName: 'time-off'
          },
          {
            accessor: 'group',
            Header: 'Group',
            minWidth: 160,
            maxWidth: 160
          },
          {
            accessor: 'subGroup',
            Header: 'Sub Group',
            minWidth: 225,
            maxWidth: 225
          },
          {
            accessor: 'penalisingStatus',
            Header: 'Penalising Status',
            minWidth: 140,
            maxWidth: 140
          }
        ]}
        defaultSorted={[
          {
            id: 'timeOn',
            desc: false
          },
          ...(showScadaObjectsColumn
            ? [
                {
                  id: 'scadaObjectKey',
                  desc: false
                }
              ]
            : [])
        ]}
      />
    )
  }
}
